import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const BRAND_COLORS = ['#CD7F32', '#a1a1aa', '#facc15', '#22d3ee', '#ffffff', '#4ade80'];

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  color: string;
  rot: number;
  vr: number;
  life: number;
  decay: number;
  shape: 'rect' | 'circle' | 'strip';
};

function spawnBurst(
  w: number,
  h: number,
  count: number,
  originX = w / 2,
  originY = h * 0.38
): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (-Math.PI / 2) + (Math.random() - 0.5) * Math.PI * 1.35;
    const speed = 7 + Math.random() * 14;
    const shapeRoll = Math.random();
    out.push({
      x: originX + (Math.random() - 0.5) * 40,
      y: originY + (Math.random() - 0.5) * 20,
      vx: Math.cos(angle) * speed * (0.7 + Math.random() * 0.6),
      vy: Math.sin(angle) * speed - 2,
      w: shapeRoll > 0.7 ? 4 + Math.random() * 4 : 6 + Math.random() * 8,
      h: shapeRoll > 0.7 ? 10 + Math.random() * 14 : 4 + Math.random() * 6,
      color: BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)],
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.35,
      life: 1,
      decay: 0.006 + Math.random() * 0.008,
      shape: shapeRoll > 0.85 ? 'circle' : shapeRoll > 0.55 ? 'strip' : 'rect',
    });
  }
  return out;
}

export function CentumthCelebration({
  answerName,
  onDone,
}: {
  answerName?: string;
  onDone: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let particles: Particle[] = [];
    let frame = 0;
    let running = true;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      onDoneRef.current();
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // Three staggered bursts for a richer payoff
    particles = spawnBurst(window.innerWidth, window.innerHeight, 90);
    const t1 = window.setTimeout(() => {
      particles.push(...spawnBurst(window.innerWidth, window.innerHeight, 55));
    }, 180);
    const t2 = window.setTimeout(() => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      particles.push(...spawnBurst(w, h, 35, w * 0.22, h * 0.42));
      particles.push(...spawnBurst(w, h, 35, w * 0.78, h * 0.42));
    }, 380);

    const tick = () => {
      if (!running) return;
      frame += 1;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      const next: Particle[] = [];
      for (const p of particles) {
        p.vy += 0.18; // gravity
        p.vx *= 0.995;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life -= p.decay;
        if (p.life <= 0 || p.y > window.innerHeight + 40) continue;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.w * 0.55, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
        next.push(p);
      }
      particles = next;

      // Auto-finish once confetti is mostly gone (or after ~4.5s)
      if ((particles.length < 8 && frame > 90) || frame > 270) {
        running = false;
        finish();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
      <motion.div
        className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => onDoneRef.current()}
        role="dialog"
        aria-label="Centumth celebration"
      >
        <div className="absolute inset-0 bg-background/55 backdrop-blur-[2px]" />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

        <motion.div
          className="relative z-10 px-6 text-center max-w-md"
          initial={{ opacity: 0, scale: 0.7, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 16, delay: 0.05 }}
        >
          <motion.div
            className="text-[10px] font-black uppercase tracking-[0.35em] text-foreground/60 mb-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            You found it
          </motion.div>

          <motion.div
            className="text-5xl sm:text-6xl font-black tracking-tight leading-none text-transparent bg-clip-text bg-[linear-gradient(to_right,#CD7F32,#a1a1aa,#facc15,#22d3ee)]"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: [0.85, 1.06, 1] }}
            transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.08 }}
          >
            CENTUMTH
          </motion.div>

          <motion.div
            className="mt-2 text-sm font-black tabular-nums tracking-widest text-foreground/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.22 }}
          >
            #100
          </motion.div>

          {answerName && (
            <motion.div
              className="mt-4 text-lg sm:text-xl font-bold leading-snug"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
            >
              {answerName}
            </motion.div>
          )}

          <motion.div
            className="mt-5 text-[11px] font-semibold text-foreground/45"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
          >
            Tap anywhere to continue
          </motion.div>
        </motion.div>
      </motion.div>
  );
}
